import moment from "moment-timezone";

/**
 * Builds a timezone-aware moment for a selected time slot.
 *
 * The date timestamp is stored using browser-local startOf("day"),
 * so we extract year/month/day using the browser timezone to get the
 * correct calendar date. The hour/minute are in the selected timezone
 * (from the availability API). We combine them into a moment in the
 * selected timezone.
 *
 * @param dateTimestamp - The stored date timestamp (start of day in browser-local time)
 * @param hour - The slot hour in the selected timezone
 * @param minute - The slot minute in the selected timezone
 * @param timezone - The candidate's selected timezone
 * @param browserTimezone - The browser's local timezone used when the date was stored
 *   (defaults to moment.tz.guess() which is correct in the browser)
 */
function buildSlotMoment(
  dateTimestamp: number,
  hour: number,
  minute: number,
  timezone: string,
  browserTimezone: string = moment.tz.guess()
): moment.Moment {
  const dateMoment = moment.tz(dateTimestamp, browserTimezone);
  return moment.tz(
    [dateMoment.year(), dateMoment.month(), dateMoment.date(), hour, minute],
    timezone
  );
}

/**
 * Computes the ISO string for a selected time slot.
 *
 * @param dateTimestamp - The stored date timestamp (start of day in browser-local time)
 * @param hour - The slot hour in the selected timezone
 * @param minute - The slot minute in the selected timezone
 * @param timezone - The candidate's selected timezone
 * @param browserTimezone - The browser's local timezone (defaults to moment.tz.guess())
 * @returns ISO 8601 string representing the correct absolute UTC moment
 */
export function computeSelectedSlotISO(
  dateTimestamp: number,
  hour: number,
  minute: number,
  timezone: string,
  browserTimezone?: string
): string {
  return buildSlotMoment(
    dateTimestamp,
    hour,
    minute,
    timezone,
    browserTimezone
  ).toISOString();
}

/**
 * Computes a locale-formatted string for a selected time slot,
 * formatted in the selected timezone.
 *
 * Used by Dialogs.tsx for display in a Chip label.
 *
 * @param dateTimestamp - The stored date timestamp (start of day in browser-local time)
 * @param hour - The slot hour in the selected timezone
 * @param minute - The slot minute in the selected timezone
 * @param timezone - The candidate's selected timezone
 * @param locale - The moment locale string for formatting
 * @param browserTimezone - The browser's local timezone (defaults to moment.tz.guess())
 * @returns Locale-formatted string like "Wed, Apr 1, 2026 7:30 AM"
 */
export function computeSelectedSlotFormatted(
  dateTimestamp: number,
  hour: number,
  minute: number,
  timezone: string,
  locale: string,
  browserTimezone?: string
): string {
  return buildSlotMoment(
    dateTimestamp,
    hour,
    minute,
    timezone,
    browserTimezone
  )
    .locale(locale)
    .format("llll");
}
